import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft } from 'lucide-react';

const TermoCompromissoPage = () => {
  return (
    <>
      <Helmet>
        <title>Termo de Compromisso e Inscrição | Kairós</title>
        <meta name="description" content="Termo de compromisso e inscrição do 2º Encontro de Casais do Seminário Psicoteológico Kairós." />
      </Helmet>

      <div className="min-h-screen bg-obsidian" data-testid="termo-compromisso-page">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-obsidian/90 backdrop-blur-md border-b border-white/5">
          <div className="container mx-auto px-4 h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <ArrowLeft className="w-5 h-5 text-gray-400" />
              <img src="/logo-kairos.png" alt="Kairós" className="w-10 h-10 rounded-full object-cover" />
              <span className="font-display text-xl text-white">Kairós</span>
            </Link>
          </div>
        </nav>

        {/* Content */}
        <main className="pt-24 pb-20">
          <div className="container mx-auto px-4 max-w-4xl">
            <article className="prose prose-invert prose-lg max-w-none">
              <p className="text-gray-400 text-sm mb-2">Última atualização: 01 de março de 2026</p>
              
              <h1 className="font-display text-3xl md:text-4xl text-white mb-4">
                Termo de Compromisso e Inscrição
              </h1>

              <div className="mb-8 p-6 bg-kairos-orange/10 border border-kairos-orange/20 rounded-xl">
                <p className="text-xl text-white font-semibold mb-1">2º Encontro de Casais – Seminário Psicoteológico Kairós</p>
                <p className="text-kairos-orange italic">"Extraindo o melhor do casamento"</p>
              </div>

              <section className="mb-10">
                <h2 className="text-xl md:text-2xl text-white font-semibold mb-4">I – Compromisso</h2>
                <p className="text-gray-300 leading-relaxed mb-4">O casal participante declara estar ciente de que:</p>
                <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                  <li>A inscrição somente será confirmada após pagamento.</li>
                  <li>O evento ocorrerá de 09 a 12 de outubro de 2026, no Hotel Fazenda Minuano.</li>
                  <li>A inscrição garante hospedagem e alimentação conforme descrito neste termo.</li>
                  <li>O evento é exclusivo para casais inscritos.</li>
                  <li>Não é permitida a participação de crianças.</li>
                  <li>Não é permitida a participação/hospedagem de terceiros não inscritos.</li>
                  <li>Não é permitida a presença de animais.</li>
                  <li>O participante deve respeitar as normas do hotel e da organização.</li>
                  <li>Cabe ao casal participante o cuidado e pudor nas vestimentas ao fazer uso das piscinas e demais dependências do hotel.</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-xl md:text-2xl text-white font-semibold mb-4">II – Hospedagem</h2>
                <p className="text-gray-300 leading-relaxed mb-4">Inclui:</p>
                <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                  <li>3 diárias com pensão completa;</li>
                  <li>Check-in: 09/10/2026 a partir das 15h;</li>
                  <li>Check-out: 12/10/2026 até 14h;</li>
                  <li>3 cafés da manhã, 3 almoços e 3 jantares;</li>
                  <li>Acesso à estrutura de lazer do hotel.</li>
                </ul>
                <p className="text-gray-300 leading-relaxed mt-4">
                  Despesas extras são de responsabilidade do participante.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-xl md:text-2xl text-white font-semibold mb-4">III – Investimento</h2>
                <p className="text-gray-300 leading-relaxed">
                  Valor da inscrição: <strong className="text-white">R$ 3.300,00</strong> (três mil e trezentos reais), conforme modalidade escolhida.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-xl md:text-2xl text-white font-semibold mb-4">IV – Forma de Pagamento</h2>
                <p className="text-gray-300 leading-relaxed mb-4">Pagamento poderá ser:</p>
                <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                  <li>À vista;</li>
                  <li>Parcelado em até 12 vezes.</li>
                </ul>
                <p className="text-gray-300 leading-relaxed mt-4">
                  A inscrição será confirmada somente após compensação do pagamento.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-xl md:text-2xl text-white font-semibold mb-4">V – Das Eventuais Desistências</h2>
                <p className="text-gray-300 leading-relaxed mb-4">Em caso de cancelamento:</p>
                <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                  <li>Até 90 dias antes do evento: devolução de 40% do valor pago.</li>
                  <li>Até 30 dias antes do evento: devolução de 20% do valor pago.</li>
                  <li>Após esse prazo: não haverá devolução.</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-xl md:text-2xl text-white font-semibold mb-4">VI – Disposições Gerais</h2>
                <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                  <li>As vagas são limitadas.</li>
                  <li>O organizador poderá criar lista de espera.</li>
                  <li>Casos omissos serão resolvidos pela organização.</li>
                  <li>O participante declara estar de acordo com todas as cláusulas.</li>
                </ul>
              </section>
            </article>

            {/* Back link */}
            <div className="mt-12 pt-8 border-t border-white/10">
              <Link to="/" className="inline-flex items-center gap-2 text-kairos-orange hover:text-kairos-orange-light transition-colors">
                <ArrowLeft className="w-4 h-4" />
                Voltar para o início
              </Link>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="py-8 border-t border-white/5">
          <div className="container mx-auto px-4 text-center">
            <p className="text-gray-500 text-sm">
              © 2026 Seminário Psicoteológico Kairós. Todos os direitos reservados.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default TermoCompromissoPage;
